import { useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LifeBuoy, Loader2 } from "lucide-react";

/**
 * Floating "Report issue" button — visible site-wide for logged-in users.
 * Captures a free-text report + the current page URL, pushes an in-app
 * notification to admin (no email per product spec). Admin can resolve from
 * the new Issues tab on the moderation panel.
 */
export default function ReportIssueButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  // Don't show on the live-room page where it would float over Jitsi controls
  if (/^\/lectures\/[^/]+\/live$/.test(location.pathname)) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/issues", {
        body: body.trim(),
        page_url: location.pathname + location.search,
      });
      toast.success("Thanks — we've been notified and will take a look.");
      setBody("");
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-30 h-12 px-4 rounded-full bg-navy text-white shadow-lg hover:bg-teal-700 transition-all flex items-center gap-2 text-sm font-medium"
        title="Report an issue"
        data-testid="report-issue-fab"
      >
        <LifeBuoy className="h-4 w-4"/>
        <span className="hidden sm:inline">Report issue</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="report-issue-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy">Report an issue</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Tell us what's not working — the page you were on, what you expected, and what happened. The admin team will be notified directly.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Textarea
              rows={6}
              value={body}
              maxLength={2000}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Describe the issue in detail…"
              required
              data-testid="report-issue-body"
            />
            <div className="text-[11px] text-slate-500 flex items-center justify-between">
              <span>Page: <code className="bg-slate-100 px-1 rounded">{location.pathname}</code></span>
              <span>{body.length} / 2000</span>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="report-issue-cancel">Cancel</Button>
              <Button type="submit" disabled={busy || !body.trim()} className="bg-teal hover:bg-teal-700 text-white" data-testid="report-issue-submit">
                {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Sending…</> : "Send report"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
